/** @format */

import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL } from "../lib/constants";
import {
  getAccessToken,
  getRefreshToken,
  setTokens as storeTokens,
  clearTokens,
} from "../utils/auth";

export interface ApiError {
  detail?: string;
  [key: string]: any;
}

export class ApiErrorResponse extends Error {
  public status: number;
  public data: ApiError;
  constructor(message: string, status: number, data: ApiError) {
    super(message);
    this.name = "ApiErrorResponse";
    this.status = status;
    this.data = data;
  }
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const redirectToLogin = () => {
  if (typeof window !== "undefined") {
    window.location.assign("/login");
  }
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    const isAuthEndpoint =
      config.url?.includes("/auth/token/") ||
      config.url?.includes("/auth/token/refresh/");
    if (token && !isAuthEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status !== 401 || originalRequest._retry) {
      const errorData = (error.response?.data as ApiError) || {};
      throw new ApiErrorResponse(
        errorData.detail || error.message || "An error occurred",
        error.response?.status || 0,
        errorData,
      );
    }

    if (
      originalRequest.url?.includes("/auth/token/") ||
      originalRequest.url?.includes("/auth/token/refresh/")
    ) {
      const errorData = (error.response?.data as ApiError) || {};
      throw new ApiErrorResponse(
        errorData.detail || "Authentication failed",
        401,
        errorData,
      );
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearTokens();
      redirectToLogin();
      throw new ApiErrorResponse(
        "Sesioni juaj ka skaduar. Ju lutem logohuni perseri.",
        401,
        {},
      );
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => apiClient(originalRequest))
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      console.log("Attempting to refresh token...");
      const response = await axios.post<{ access: string; refresh: string }>(
        `${API_BASE_URL}/auth/token/refresh/`,
        { refresh: refreshToken },
      );
      const { access, refresh } = response.data;
      console.log("Token refresh successful");
      storeTokens(access, refresh, true);
      originalRequest.headers.Authorization = `Bearer ${access}`;
      processQueue();
      isRefreshing = false;
      return apiClient(originalRequest);
    } catch (refreshError: any) {
      console.error("Token refresh failed:", refreshError);
      processQueue(refreshError);
      isRefreshing = false;
      clearTokens();
      redirectToLogin();
      throw new ApiErrorResponse(
        "Sesioni juaj ka skaduar. Ju lutem logohuni perseri",
        401,
        {},
      );
    }
  },
);
